import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const fetchPlaytestArenaFilesCallable = httpsCallable(
  functions,
  'fetchPlaytestArenaFiles',
);

/**
 * @param {string} companyId
 * @param {string} gameId
 * @returns {string}
 */
export function playtestArenaCacheKey(companyId, gameId) {
  return `shiftypm.playtestArena.${companyId}.${gameId}`;
}

/**
 * Clear any locally cached Playtest Arena payloads for a project.
 * @param {string} companyId
 * @param {string} gameId
 */
export function clearPlaytestArenaLocalCache(companyId, gameId) {
  if (typeof localStorage === 'undefined') return;
  const cid = typeof companyId === 'string' ? companyId.trim() : '';
  const gid = typeof gameId === 'string' ? gameId.trim() : '';
  if (!cid || !gid) return;
  try {
    localStorage.removeItem(playtestArenaCacheKey(cid, gid));
  } catch (err) {
    console.warn('clearPlaytestArenaLocalCache failed:', err);
  }
}

/**
 * Wipe every Playtest Arena cache entry (all projects).
 */
export function clearAllPlaytestArenaLocalCaches() {
  if (typeof localStorage === 'undefined') return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith('shiftypm.playtestArena.')) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    console.warn('clearAllPlaytestArenaLocalCaches failed:', err);
  }
}

/**
 * @param {unknown} raw
 * @returns {Array<{ id: string, path: string, content: string }>}
 */
function normalizeArenaFiles(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof row === 'object')
    .map((row, index) => {
      const path =
        typeof row.path === 'string'
          ? row.path.trim().replace(/\\/g, '/')
          : '';
      const content =
        typeof row.content === 'string'
          ? row.content
          : typeof row.patch_content === 'string'
            ? row.patch_content
            : typeof row.source === 'string'
              ? row.source
              : '';
      if (!path || !String(content).trim()) return null;
      return {
        id:
          typeof row.id === 'string' && row.id.trim()
            ? row.id.trim()
            : path || `file_${index}`,
        path,
        content,
      };
    })
    .filter(Boolean);
}

/**
 * Load Phaser engine files from the project's GitHub main tip (cache-busted).
 * Never hydrates Phaser from localStorage — stale platformer caches caused
 * texture crashes when switching games (e.g. Pong).
 *
 * @param {{
 *   companyId?: string | null,
 *   gameId?: string | null,
 *   enabled?: boolean,
 *   hasGithubRepo?: boolean,
 *   refreshToken?: number,
 * }} args
 */
export function usePlaytestArenaFiles({
  companyId = '',
  gameId = '',
  enabled = true,
  hasGithubRepo = false,
  refreshToken = 0,
} = {}) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const requestSeq = useRef(0);

  const reload = useCallback(async () => {
    const cid = typeof companyId === 'string' ? companyId.trim() : '';
    const gid = typeof gameId === 'string' ? gameId.trim() : '';
    if (!enabled || !hasGithubRepo || !cid || !gid) {
      setFiles([]);
      setMeta(null);
      setError('');
      setLoading(false);
      return [];
    }

    // Always clear local cache so Force Refresh / remount never boots stale code.
    clearPlaytestArenaLocalCache(cid, gid);
    setFiles([]);
    setMeta(null);

    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      const cacheBust = Date.now();
      const result = await fetchPlaytestArenaFilesCallable({
        gameId: gid,
        cacheBust,
      });
      if (seq !== requestSeq.current) return [];

      const payload = result?.data || {};
      const nextFiles = normalizeArenaFiles(payload.files);
      const nextMeta = {
        owner: payload.owner || '',
        repo: payload.repo || '',
        branch: payload.branch || 'main',
        commitSha: payload.commitSha || '',
        fetchedAt: payload.fetchedAt || new Date().toISOString(),
        cacheBust: payload.cacheBust || cacheBust,
        fileCount: nextFiles.length,
      };
      setFiles(nextFiles);
      setMeta(nextMeta);
      return nextFiles;
    } catch (err) {
      if (seq !== requestSeq.current) return [];
      const message =
        err?.message ||
        err?.details ||
        'Could not fetch Playtest Arena files from GitHub.';
      console.error('usePlaytestArenaFiles:', err);
      setError(String(message));
      setFiles([]);
      setMeta(null);
      return [];
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [companyId, gameId, enabled, hasGithubRepo]);

  useEffect(() => {
    void reload();
  }, [reload, refreshToken]);

  return {
    files,
    loading,
    error,
    meta,
    reload,
  };
}
